import React, { useState, useEffect } from 'react';
import { Phone, MapPin, Clock, Star, Wrench, Droplets, FileText, ArrowRightLeft, Settings, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { testimonials } from '../mock';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    { icon: Clock, title: '12+ Years Experience', description: 'Trusted expertise since 2014' },
    { icon: Star, title: '3000+ Happy Customers', description: 'Repeated & satisfied clients' },
    { icon: Settings, title: 'BS-6 Technology', description: 'Latest vehicle servicing' },
    { icon: Wrench, title: 'Honest Service', description: 'Transparent pricing always' },
    { icon: MapPin, title: 'Pick up & Drop', description: 'Leave your vehicle, rest is our responsibility' },
  ];

  const services = [
    { icon: Wrench, title: '2 Wheeler Repairing', description: 'Expert repairs for all brands' },
    { icon: Droplets, title: 'Washing & Detailing', description: 'Professional cleaning services' },
    { icon: FileText, title: 'Insurance & RTO', description: 'Complete documentation support' },
    { icon: ArrowRightLeft, title: 'Sale, Buy & Exchange', description: 'Fair valuation & hassle-free process. Your trust is our priority' },
    { icon: Settings, title: 'Complete Servicing', description: 'Every Possible Solution for Every Possible Problem - That\'s Our Commitment!' },
  ];

  return (
    <div className="min-h-screen bg-white relative">
      {/* Background Watermark Logo */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0 overflow-hidden">
        <img 
          src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/6fiusach_29318.jpg"
          alt="Watermark"
          className="w-96 h-96 md:w-[600px] md:h-[600px] opacity-5 object-contain"
        />
      </div>
      {/* Header/Navigation */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm shadow-md z-50 relative">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/6fiusach_29318.jpg" 
                alt="SK Automobiles Logo" 
                className="h-16 w-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-blue-600">SK AUTOMOBILES</h1>
                <p className="text-sm text-gray-600 italic">More Life To Your Vehicle</p>
              </div>
            </div>
            <nav className="hidden md:flex gap-6">
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-600 transition-colors">About</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-blue-600 transition-colors">Services</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 hover:text-blue-600 transition-colors">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-600 transition-colors">Contact</button>
            </nav>
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white hidden md:flex"
              onClick={() => window.location.href = 'tel:9764272788'}
            >
              <Phone className="mr-2 h-4 w-4" />
              Call Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden mt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1636761358757-0a616eb9e17e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxtb3RvcmN5Y2xlJTIwbWVjaGFuaWN8ZW58MHx8fHwxNzcwNzk5Mjg5fDA&ixlib=rb-4.1.0&q=85"
            alt="Garage Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-blue-800/80"></div>
        </div>
        
        <div className={`relative z-10 text-center px-4 transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            From Minor Fixes to<br />Major Repairs, We Handle It All
          </h2>
          <p className="text-xl md:text-2xl text-white/90 mb-8">
            Your Trusted 2-Wheeler Service Center in Nagpur
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white text-lg px-8"
              onClick={() => window.location.href = 'tel:9764272788'}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call: 9764272788
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-blue-600 text-lg px-8"
              onClick={() => window.location.href = 'https://wa.me/919112228786'}
            >
              WhatsApp Enquiry
            </Button>
          </div>
          <button 
            onClick={() => scrollToSection('founder')}
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce text-white"
          >
            <ChevronDown className="h-8 w-8" />
          </button>
        </div>
      </section>

      {/* Founder Section */}
      <section id="founder" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden border-4 border-blue-600 shadow-xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/umu5ewdj_29867.jpg"
                  alt="Imran Sheikh - Owner & Founder"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-2">Imran Sheikh</h3>
              <p className="text-xl text-blue-600 font-semibold">Owner & Founder</p>
              <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
                With over 12 years of experience in automobile servicing, Imran Sheikh founded SK Automobiles 
                with a vision to provide honest, reliable, and professional vehicle care to the people of Nagpur.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">About SK Automobiles</h2>
              <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
              <p className="text-lg text-gray-600 max-w-4xl mx-auto leading-relaxed">
                SK Automobiles is a premier garage located in Jaripatka Nagpur. Founded by Imran Sheikh in 2014. Renowned for its commitment to excellence, SK Automobiles offers a wide spectrum of services including vehicle servicing, thorough washing, expert repairs, and buying and selling of used two-wheelers. With the focus of extending the life and performance of every vehicle, the garage has become a trusted destination for two-wheeler owners seeking reliable, high quality care. Over the years SK Automobiles has earned a reputation for professional service, attention to details, and customer satisfaction, making it one of the most celebrated garages in the city.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/q0p1r790_29698.jpg"
                  alt="SK Automobiles Garage"
                  className="rounded-lg shadow-2xl"
                />
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">12+ Years of Experience</h3>
                    <p className="text-gray-600">Serving the Nagpur community with dedication and excellence since 2014.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Settings className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">BS-6 Technology Specialists</h3>
                    <p className="text-gray-600">Equipped with latest tools and knowledge to service modern BS-6 compliant vehicles.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Wrench className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Highly Experienced Mechanics</h3>
                    <p className="text-gray-600">Our team of skilled technicians ensures quality work on every vehicle.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Star className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Trusted Local Garage</h3>
                    <p className="text-gray-600">Built on trust, transparency, and a customer-first approach.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Why Choose SK Automobiles</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">What makes us different from others</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <feature.icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Services</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">Complete 2-Wheeler Solutions Under One Roof</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <Card key={index} className="border-2 border-gray-100 hover:border-blue-600 transition-all duration-300 hover:shadow-xl group">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">What Our Customers Say</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
            <p className="text-xl text-gray-600">Real feedback from our valued clients</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-none shadow-lg bg-white">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic">"{testimonial.review}"</p>
                  <div>
                    <p className="font-bold text-gray-800">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Need Advice or Facing an Issue?</h2>
            <p className="text-xl text-white/90 mb-10">
              Call us anytime for free guidance and expert consultation. We're here to help!
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white text-lg px-8 py-6"
                onClick={() => window.location.href = 'tel:9764272788'}
              >
                <Phone className="mr-2 h-6 w-6" />
                Call Imran Sheikh: 9764272788
              </Button>
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6"
                onClick={() => window.location.href = 'https://wa.me/919112228786'}
              >
                WhatsApp: 9112228786
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Visit Us</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto mb-6"></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Our Location</h3>
                  <p className="text-gray-600">
                    Barakholi Square, Jaripatka<br />
                    Main Road, Nagpur, Maharashtra
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Contact Numbers</h3>
                  <p className="text-gray-600">
                    Imran Sheikh: <a href="tel:9764272788" className="text-blue-600 hover:underline">9764272788</a><br />
                    Enquiry/WhatsApp: <a href="tel:9112228786" className="text-blue-600 hover:underline">9112228786</a>
                  </p>
                </div>
              </div>
              
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Working Hours</h3>
                  <p className="text-gray-600">
                    Monday - Sunday<br />
                    10:00 a.m. to 8:30 p.m.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-gray-100 rounded-lg p-8 h-full flex flex-col items-center justify-center">
                <MapPin className="h-16 w-16 text-blue-600 mb-4" />
                <p className="text-gray-800 font-semibold mb-4">Visit Us on Google Maps</p>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={() => window.open('https://www.google.com/search?sca_esv=9459100d7edd2511&hl=en-US&gl=in&output=search&kgmid=%2Fg%2F11f3nm6zbc&q=SK%20AUTOMOBILES&source=sh%2Fx%2Floc%2Fact%2Fm1%2F4&kgs=e5c688ab490a9549&shndl=30&shem=sume', '_blank')}
                >
                  <MapPin className="mr-2 h-4 w-4" />
                  View Location
                </Button>
              </div>
            </div>
          </div>
          
          {/* Gallery Section */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">Garage Photos</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/en5f42g3_29695.jpg"
                  alt="Royal Enfield Bikes at SK Automobiles"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/4rck57h9_29699.jpg"
                  alt="Expert Mechanic at Work"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/a7ymr2mh_29701.jpg"
                  alt="Professional Scooter Servicing"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/q0p1r790_29698.jpg"
                  alt="SK Automobiles Garage Exterior"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Floating Call Button */}
      <div className="fixed bottom-6 right-6 z-50 md:hidden">
        <Button 
          size="lg"
          className="bg-red-600 hover:bg-red-700 text-white shadow-2xl rounded-full w-16 h-16 p-0"
          onClick={() => window.location.href = 'tel:9764272788'}
        >
          <Phone className="h-6 w-6" />
        </Button>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 relative">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_sk-auto-service/artifacts/6fiusach_29318.jpg" 
                alt="SK Automobiles Logo" 
                className="h-16 w-16 object-contain mb-4"
              />
              <h3 className="text-xl font-bold mb-2">SK AUTOMOBILES</h3>
              <p className="text-gray-400 text-sm">Serving Nagpur with Pride</p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('testimonials')} className="hover:text-white transition-colors">Testimonials</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Our Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>2 Wheeler Repairing</li>
                <li>Washing & Detailing</li>
                <li>Insurance & RTO Work</li>
                <li>Vehicle Exchange</li>
                <li>Complete Servicing</li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Barakholi Square, Jaripatka</li>
                <li>Main Road, Nagpur</li>
                <li className="pt-2">
                  <a href="tel:9764272788" className="hover:text-white transition-colors">📞 9764272788</a>
                </li>
                <li>
                  <a href="tel:9112228786" className="hover:text-white transition-colors">📞 9112228786</a>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; 2024 SK Automobiles. All rights reserved. | More Life To Your Vehicle</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
